import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import Sidebar from "./Sidebar";

const COLLAPSE_KEY = "gv_sidebar_collapsed";

export default function Layout({ children, title, subtitle, action }) {
  const [open, setOpen] = useState(false);
  // Desktop-only collapsed state. Persisted to localStorage so it survives
  // page reloads. Mobile drawer is unaffected (it always opens at full width).
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch (e) {
      console.warn("Could not read sidebar collapsed state:", e);
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch (e) {
      console.warn("Could not persist sidebar collapsed state:", e);
    }
  }, [collapsed]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-[#EAEAEA]">
      {/* Desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">
        <Sidebar
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
        />
      </div>

      {/* Mobile drawer (always expanded — no collapse on mobile per spec) */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="relative z-10">
            <Sidebar onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-30">
          <div className="px-5 lg:px-12 py-5 flex items-center gap-4">
            <button
              className="lg:hidden text-[#EAEAEA]"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" strokeWidth={1.25} />
            </button>
            <div className="flex-1 min-w-0">
              {subtitle && <div className="overline" data-testid="page-subtitle">{subtitle}</div>}
              <h1 className="serif text-3xl md:text-4xl mt-1 tracking-tight" data-testid="page-title">
                {title}
              </h1>
            </div>
            {action && <div className="shrink-0">{action}</div>}
          </div>
        </header>

        <main className="px-5 lg:px-12 py-8 lg:py-12 max-w-[1600px]">
          {children}
        </main>
      </div>
    </div>
  );
}
